// ═══════════════════════════════════════════════════════════════════
//  LiveFilterContext — per-page subscription to active search hits.
// ═══════════════════════════════════════════════════════════════════
//
//  Keeps SearchContext (the global query state) decoupled from the
//  page-level matched-id sets. The current canvas / page registers
//  itself with `setActiveSourceId` so `matchedIdsForCurrent` returns
//  only the hits relevant to what's on screen.
//
// ═══════════════════════════════════════════════════════════════════

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { SearchHit } from '@/types/search';

interface LiveFilterContextValue {
  /** Currently active query (mirrors SearchContext.query). */
  activeQuery: string;
  /** All hits from the most recent runSearch invocation. */
  hits: SearchHit[];
  /** Set of entity ids that currently match. */
  matchedIds: ReadonlySet<string>;
  /** Source id (workflow id or atlas id) the page is currently showing. */
  activeSourceId: string | null;
  /** Subset of matchedIds limited to the active sourceId. */
  matchedIdsForCurrent: ReadonlySet<string>;
  /** True when activeQuery non-empty AND we have data to filter against. */
  isFiltering: boolean;

  setActiveQuery: (q: string) => void;
  setHits: (hits: SearchHit[]) => void;
  setActiveSourceId: (id: string | null) => void;
}

const LiveFilterContext = createContext<LiveFilterContextValue | null>(null);

export function LiveFilterProvider({ children }: { children: ReactNode }) {
  const [activeQuery, setActiveQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [activeSourceId, setActiveSourceId] = useState<string | null>(null);

  const matchedIds = useMemo(() => new Set(hits.map((h) => h.id)), [hits]);
  const matchedIdsForCurrent = useMemo(() => {
    if (!activeSourceId) return matchedIds;
    return new Set(
      hits.filter((h) => h.sourceId === activeSourceId).map((h) => h.id),
    );
  }, [hits, activeSourceId, matchedIds]);

  const isFiltering = activeQuery.trim().length > 0;

  const value = useMemo<LiveFilterContextValue>(
    () => ({
      activeQuery,
      hits,
      matchedIds,
      activeSourceId,
      matchedIdsForCurrent,
      isFiltering,
      setActiveQuery,
      setHits,
      setActiveSourceId,
    }),
    [activeQuery, hits, matchedIds, activeSourceId, matchedIdsForCurrent, isFiltering],
  );

  return (
    <LiveFilterContext.Provider value={value}>
      {children}
    </LiveFilterContext.Provider>
  );
}

export function useLiveFilter(): LiveFilterContextValue {
  const ctx = useContext(LiveFilterContext);
  if (!ctx) {
    throw new Error('useLiveFilter() must be called inside <LiveFilterProvider>');
  }
  return ctx;
}

/**
 * Tiny helper for canvas / list components: returns matched ids
 * relevant to the given sourceId (e.g. a specific workflow). Returns
 * an empty set when not filtering, so callers can render normally.
 */
export function useLiveFilterMatches(sourceId: string | null): ReadonlySet<string> {
  const { hits, isFiltering } = useLiveFilter();
  return useMemo(() => {
    if (!isFiltering || !sourceId) return new Set();
    return new Set(
      hits.filter((h) => h.sourceId === sourceId).map((h) => h.id),
    );
  }, [hits, isFiltering, sourceId]);
}

/**
 * One-line registration: a page or canvas can call this to declare
 * "I'm showing source X" so LiveFilterContext can scope hits.
 */
export function useRegisterActiveSource(id: string | null): void {
  const { setActiveSourceId } = useLiveFilter();
  useEffect(() => {
    setActiveSourceId(id);
    return () => setActiveSourceId(null);
  }, [id, setActiveSourceId]);
}
